import React, {useContext} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import RichText from "../../components/RichText";
import {fetchAction, withAuthCheck, AuthError} from "../../services/api";
import {AuthContext} from "../../contexts/AuthContext";
import {useSnackbar} from "../../contexts/SnackbarContext";
import { useTranslation } from 'react-i18next';
import { useV3StateContext } from '../V3StateContext';

/**
 * @param {string} iconName - Название иконки (файл), например "house", "mushroom" и т.д.
 * @param {string} text - Текст, который может быть однострочным или двухстрочным.
 * @param {string} buttonLabel - Текст на кнопке.
 * @param {string} buttonColor - Цвет кнопки (hex, rgb, etc.).
 * @param {string} actionId - ID действия, которое будет выполняться по клику на кнопку.
 */
export default function V3OpsWidget({
                                      iconName = '',
                                      text = '',
                                      buttonLabel,
                                      buttonColor = '#1976d2', // По умолчанию синий
                                      textColor = '#ffffff',
                                      actionId = '',
                                  }) {
    const token = useContext(AuthContext);
    const { showSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const { setState } = useV3StateContext();
    const [isLoading, setIsLoading] = React.useState(false);

    // Track widget updates
    React.useEffect(() => {
        // Widget re-rendered
    });

    const displayButtonLabel = buttonLabel || t('widgets.ops.defaultButton');

    const handleButtonClick = () => {
        setIsLoading(true);
        withAuthCheck(fetchAction, token, actionId)
            .then(data => {
                showSnackbar(data.result, "success");
                
                // If the response includes updated OpsWidget data, update the state
                if (data.opsWidget) {
                    setState(prev => ({
                        ...prev,
                        regionCard: {
                            ...prev.regionCard,
                            opsWidget: data.opsWidget
                        }
                    }));
                }
                
                // The backend should call updateV3Region after successful BUILD action
                // This will trigger an SSE update with the new OpsWidget data
                // For now, the widget will update via SSE within 10 seconds
            })
            .catch(err => {
                if (err instanceof AuthError) {
                    // WebAuthForm покажется автоматически через AuthProvider
                    return;
                }
                const errorMessage = err.result || err.message || "error";
                showSnackbar(errorMessage, "error");
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                p: 2,
                boxSizing: 'border-box',
                width: '100%',
                backgroundColor: 'transparent',
            }}
        >
            {/* Левая часть: иконка фиксированного размера */}
            {/* Левая часть: Иконка занимает 10% */}
            <Box
                sx={{
                    width: '20%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <img
                    src={`/img/${iconName}.webp`}
                    alt={iconName}
                    style={{ width: '100%', height: 'auto' }}
                />
            </Box>

            {/* Центральная часть: текст, растягивается, центрируется по горизонтали */}
            <Box
                sx={{
                    width: '50%',
                    display: 'flex',
                    justifyContent: 'left',
                    alignItems: 'left',
                    textAlign: 'left',
                    px: 2,
                }}
            >
                <Typography
                    variant="body2"
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    <RichText content={text} />
                </Typography>
            </Box>

            {/* Правая часть: Кнопка занимает 30% */}
            <Box
                sx={{
                    width: '30%',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                }}
            >
                <Button
                    fullWidth
                    variant="contained"
                    onClick={handleButtonClick}
                    disabled={isLoading}
                    sx={{
                        backgroundColor: buttonColor,
                        color: textColor,
                        '&:hover': {
                            backgroundColor: buttonColor,
                        },
                        '&:disabled': {
                            backgroundColor: buttonColor,
                            opacity: 0.7,
                        },
                        overflow: 'hidden',
                    }}
                >
                    {isLoading ? '...' : displayButtonLabel}
                </Button>
            </Box>
        </Box>
    );
}